/**
 * SnapshotPreviewPanel — Feature 5 snapshot preview in right sidebar
 *
 * Replaces modal HistorySnapshotDialog with integrated PreviewPanel experience.
 * Shows snapshot content/diff with navigation (← →), comparison mode toggle,
 * and action buttons (tag/delete/restore).
 */

import { useCallback, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { useEditorStore } from "../../store/editor-store";
import { useHistoryStore } from "../../store/history-store";
import { getPlatformService } from "../../platform";
import { SnapshotPreviewHeader } from "./SnapshotPreviewHeader";
import { SnapshotPreviewContent } from "./SnapshotPreviewContent";
import { SnapshotPreviewFooter } from "./SnapshotPreviewFooter";

export function SnapshotPreviewPanel() {
    const { 
        snapshotPreviewState, 
        navigateSnapshot, 
        setSnapshotComparisonMode, 
        clearSnapshotPreview,
        setSnapshotPreview,
        tabs,
        activeTabId,
    } =
        useEditorStore(useShallow((s) => ({
            snapshotPreviewState: s.snapshotPreviewState,
            navigateSnapshot: s.navigateSnapshot,
            setSnapshotComparisonMode: s.setSnapshotComparisonMode,
            clearSnapshotPreview: s.clearSnapshotPreview,
            setSnapshotPreview: s.setSnapshotPreview,
            tabs: s.tabs,
            activeTabId: s.activeTabId,
        })));
    const { filePath, currentSnapshotId, comparisonMode, enabled } = snapshotPreviewState;

    // Get snapshot metadata from history store using shallow comparison
    // This prevents unnecessary re-renders when array content hasn't changed
    const snapshotList = useHistoryStore(
        useShallow((s) => s.getSnapshotList(filePath ?? ""))
    );
    const currentSnapshot = useMemo(() => {
        return snapshotList.find((s) => s.id === currentSnapshotId) ?? null;
    }, [snapshotList, currentSnapshotId]);

    // Determine position in list
    const currentIndex = useMemo(() => {
        return snapshotList.findIndex((s) => s.id === currentSnapshotId);
    }, [snapshotList, currentSnapshotId]);

    const canNavigatePrev = currentIndex > 0;
    const canNavigateNext = currentIndex >= 0 && currentIndex < snapshotList.length - 1;

    // Navigation handlers
    const onNavigate = useCallback(
        (direction: "prev" | "next") => {
            navigateSnapshot(direction, snapshotList);
        },
        [navigateSnapshot, snapshotList],
    );

    const onClose = useCallback(() => {
        clearSnapshotPreview();
    }, [clearSnapshotPreview]);

    const onModeChange = useCallback(
        (mode: "content" | "diff") => {
            setSnapshotComparisonMode(mode);
        },
        [setSnapshotComparisonMode],
    );

    // Get current document content from active tab
    const getCurrentContent = useCallback(async () => {
        try {
            // Get content from the active tab
            const activeTab = tabs.find((t) => t.id === activeTabId);
            if (activeTab) {
                return activeTab.content;
            }
            return "";
        } catch (error) {
            console.error("Failed to get current file content:", error);
            return "";
        }
    }, [tabs, activeTabId]);

    // Handle restore success — optionally navigate to preSnapshot
    const handleRestoreSuccess = useCallback(
        (preSnapshotId?: string) => {
            if (preSnapshotId && filePath) {
                // Navigate to the pre-restore snapshot to show what was there before
                setSnapshotPreview(filePath, preSnapshotId);
            }
        },
        [filePath, setSnapshotPreview],
    );

    // Load snapshot data on demand
    const loadSnapshotContent = useCallback(
        async (snapshotId: string) => {
            try {
                const result = await getPlatformService().engine.historyGet({
                    filePath: filePath ?? "",
                    snapshotId,
                });
                return result.content;
            } catch (err) {
                console.error(`Failed to load snapshot ${snapshotId}:`, err);
                return null;
            }
        },
        [filePath],
    );

    const loadSnapshotDiff = useCallback(
        async (snapshotId: string, currentContent: string) => {
            try {
                const result = await getPlatformService().engine.historyDiff({
                    filePath: filePath ?? "",
                    fromSnapshotId: snapshotId,
                    currentContent,
                });
                return result.unifiedDiff;
            } catch {
                return null;
            }
        },
        [filePath],
    );

    // Guard check AFTER all hooks are declared
    if (!enabled || !filePath || !currentSnapshotId || !currentSnapshot) {
        return null;
    }

    return (
        <div className="flex flex-col h-full bg-background">
            {/* Header: metadata + tabs + navigation */}
            <SnapshotPreviewHeader
                snapshot={currentSnapshot}
                currentIndex={currentIndex}
                totalSnapshots={snapshotList.length}
                onNavigatePrev={() => onNavigate("prev")}
                onNavigateNext={() => onNavigate("next")}
                onClose={onClose}
                canNavigatePrev={canNavigatePrev}
                canNavigateNext={canNavigateNext}
                comparisonMode={comparisonMode}
                onModeChange={onModeChange}
            />

            {/* Content: view area */}
            <SnapshotPreviewContent
                snapshot={currentSnapshot}
                snapshotId={currentSnapshotId!}
                filePath={filePath}
                comparisonMode={comparisonMode}
                loadContent={loadSnapshotContent}
                loadDiff={loadSnapshotDiff}
            />

            {/* Footer: actions (tag/delete/restore) + menu */}
            <SnapshotPreviewFooter
                snapshot={currentSnapshot}
                snapshotId={currentSnapshotId!}
                filePath={filePath}
                onSnapshotDeleted={onClose}
                getCurrentContent={getCurrentContent}
                onRestoreSuccess={handleRestoreSuccess}
            />
        </div>
    );
}
